'use client';

export default function Page() {
  return (
    <>
      <section className="section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container container-max">
          {/* Hero Section */}
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-extrabold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary ">
              Privacy Notice
            </h1>
            <p className="max-w-3xl mx-auto text-lg text-foreground/70">
              Effective Date: January 2025
            </p>
          </div>
        </div>
      </section>
      <section className="section-md">
        <div className="container space-y-12 container-md">
          <p className="mb-4">
            At PayStop, we value your privacy and are committed to protecting the personal
            information you share with us. This Privacy Notice outlines how we collect, use, store,
            and protect your personal data when you use our services, including our website and
            mobile applications (the "Platform"). By accessing or using our Platform, you consent to
            the collection and use of your personal data as described in this notice.
          </p>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">1. Information We Collect</h2>
            <div className="pl-4">
              <p className="mb-2">
                We collect various types of personal information to provide and improve our
                services. The information we collect includes, but is not limited to:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Personal Identifiable Information (PII):</strong> When you register as an
                  agent, customer, or admin on the Platform, we may collect information such as your
                  name, phone number, email address, date of birth, and physical address.
                </li>
                <li>
                  <strong>Transaction Information:</strong> We collect details about your
                  transactions made through PayStop, including amounts, dates, locations, and the
                  involved account information (digital banking accounts, mobile wallets, and
                  traditional bank accounts).
                </li>
                <li>
                  <strong>Device and Usage Information:</strong> When you access the Platform, we
                  collect information about your device, including your IP address, browser type,
                  operating system, and usage patterns.
                </li>
                <li>
                  <strong>Verification Information:</strong> In compliance with financial
                  regulations, we may collect information to verify your identity. This may include
                  government-issued identification, business registration details, and other
                  relevant documents.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">2. How We Use Your Information</h2>
            <div className="pl-4">
              <p className="mb-2">
                We use your personal information to provide, improve, and personalise the services
                offered through PayStop. Specifically, we may use your information for:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Account Management:</strong> To create and manage your account, process
                  transactions, and offer customer support.
                </li>
                <li>
                  <strong>Transaction Processing:</strong> To facilitate deposits, withdrawals, and
                  transfers between digital banking accounts, mobile wallets, and traditional bank
                  accounts.
                </li>
                <li>
                  <strong>Security and Fraud Prevention:</strong> To authenticate your identity,
                  detect fraudulent activity, and ensure secure transactions.
                </li>
                <li>
                  <strong>Communication:</strong> To send you important updates regarding your
                  account, such as transaction notifications, security alerts, or changes to our
                  services. We may also send you promotional emails if you've opted-in to receive
                  such communications.
                </li>
                <li>
                  <strong>Compliance:</strong> To comply with legal and regulatory requirements,
                  such as anti-money laundering (AML) and know-your-customer (KYC) regulations.
                </li>
                <li>
                  <strong>Improvement of Services:</strong> To analyse usage trends and improve the
                  overall functionality and user experience of the Platform.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">3. How We Share Your Information</h2>
            <div className="pl-4">
              <p className="mb-2">
                We respect your privacy and do not sell or rent your personal information to third
                parties. However, we may share your data in the following situations:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>With Service Providers:</strong> We may share your information with
                  trusted third-party service providers who assist us in operating the Platform and
                  delivering our services. These third parties are bound by confidentiality
                  agreements and are not allowed to use your data for any other purpose.
                </li>
                <li>
                  <strong>With Business Partners:</strong> If you use services provided by
                  third-party financial institutions, such as mobile wallets or traditional banks,
                  we may share certain information with them to facilitate transactions on your
                  behalf. This information is shared securely and only as necessary.
                </li>
                <li>
                  <strong>For Legal and Regulatory Compliance:</strong> We may disclose your
                  information when required to do so by law or to comply with legal processes, such
                  as responding to subpoenas, court orders, or regulatory inquiries.
                </li>
                <li>
                  <strong>For Fraud Prevention and Security:</strong> We may share your information
                  with law enforcement or other authorities if we believe there is a risk of
                  fraudulent activities or security breaches.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">4. How We Protect Your Information</h2>
            <div className="pl-4">
              <p className="mb-2">
                We take your privacy seriously and implement strict security measures to protect
                your personal data from unauthorised access, disclosure, alteration, or destruction.
                These measures include:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Encryption:</strong> We use encryption technology to protect sensitive
                  data, such as transaction details, when transmitted over the internet.
                </li>
                <li>
                  <strong>Access Controls:</strong> Only authorised personnel and service providers
                  with a legitimate need to access your data will have permission to do so. All
                  employees and agents are trained on data security best practices.
                </li>
                <li>
                  <strong>Regular Audits:</strong> We perform regular security audits to ensure our
                  systems are secure and up-to-date with industry standards.
                </li>
                <li>
                  <strong>Two-Factor Authentication (2FA):</strong> We offer two-factor
                  authentication to further protect your account and transactions.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">5. Your Rights and Choices</h2>
            <div className="pl-4">
              <p className="mb-2">
                You have several rights regarding your personal data, including:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Access:</strong> You have the right to request access to the personal
                  information we hold about you.
                </li>
                <li>
                  <strong>Correction:</strong> You can update or correct any inaccurate information
                  we have about you.
                </li>
                <li>
                  <strong>Deletion:</strong> In certain circumstances, you can request the deletion
                  of your personal data.
                </li>
                <li>
                  <strong>Opt-Out of Marketing Communications:</strong> If you no longer wish to
                  receive marketing emails from us, you can unsubscribe at any time by clicking the
                  unsubscribe link at the bottom of our emails.
                </li>
                <li>
                  <strong>Data Portability:</strong> You can request a copy of your personal data in
                  a structured, machine-readable format.
                </li>
              </ul>
              <p className="mb-2">
                To exercise any of these rights, please contact us using the contact details
                provided below.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">6. Retention of Data</h2>
            <div className="pl-4">
              <p className="mb-2">
                We will retain your personal information only for as long as necessary to fulfil the
                purposes outlined in this Privacy Notice, or as required by law. Once your data is
                no longer needed, we will securely delete or anonymise it.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">7. International Transfers</h2>
            <div className="pl-4">
              <p className="mb-2">
                If you are accessing the Platform from outside of Cameroon, your data may be
                transferred to and stored in servers located in other countries. By using our
                services, you consent to the transfer of your personal data to these locations.
              </p>
              <p className="mb-2">
                We take all reasonable steps to ensure that your personal data is treated securely
                and in accordance with this Privacy Notice.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">8. Cookies and Tracking Technologies</h2>
            <div className="pl-4">
              <p className="mb-2">
                We use cookies and other tracking technologies to enhance your experience on the
                Platform. Cookies are small files stored on your device that help us analyse site
                traffic and improve functionality. You can manage your cookie preferences through
                your browser settings.
              </p>
              <p className="mb-2">
                For more information on how we use cookies, please refer to our Cookie Policy.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">9. Changes to This Privacy Notice</h2>
            <div className="pl-4">
              <p className="mb-2">
                PayStop reserves the right to update or modify this Privacy Notice at any time. Any
                changes will be posted on this page, and the updated notice will become effective as
                soon as it is published. We encourage you to review this page periodically to stay
                informed about how we protect your privacy.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">10. Contact Us</h2>
            <div className="pl-4">
              <p className="mb-2">
                If you have any questions or concerns about this Privacy Notice or how your personal
                data is handled, please contact us at:
              </p>
              <p className="mb-2">Email: support@paystop.io</p>
              <p className="mb-2">Phone: +237 676962723</p>
              <p className="mb-8">
                Address: Crystal Finance Commercial Avenue Bamenda, P.O. Box 168 Bamenda Cameroon
              </p>
              <i className="mb-2">
                By using the PayStop Platform, you acknowledge that you have read and understood
                this Privacy Notice and agree to the collection, use, and sharing of your personal
                information as described herein.
              </i>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
